import { Fragment, ReactNode, useCallback } from 'react'

import { lazyRenderWidget } from 'core-components/helpers/lazyLoad'
import { Switch } from 'core-components/library/checkbox'
import { Dropdown } from 'core-components/library/dropdown'
import { NumberInput } from 'core-components/library/inputs'
import { BaseCrud, Static } from 'core-components/types'

import { IExtendedProperties } from '../models/IExtendedProperties'
import { IProperties, IPropertiesEnumValues, TPropertiesValue } from '../models/IProperties'

import { ArrayObjectRender } from './renderEditor/ArrayObjectRender'
import { DefaultRender } from './renderEditor/DefaulRender'

interface IRenderEditor {
  index: number
  property: IProperties
  properties: IExtendedProperties[]
  updateData: () => void
  renderObjectFields: (property: IProperties) => ReactNode
}

export const RenderEditor = ({
  properties,
  property,
  index,
  updateData,
  renderObjectFields,
}: IRenderEditor) => {
  const updateDataValueByIndex = useCallback(
    (i: number, value: TPropertiesValue, allProps: IProperties[]) => {
      const newData = [...allProps]
      newData[i].value = value
      updateData()
    },
    [properties, updateData]
  )

  const onChange = useCallback(
    (newValue: string) => {
      updateDataValueByIndex(index, newValue, properties)
    },
    [index, properties]
  )

  switch (property.type) {
    case 'boolean':
      return (
        <div className={'content-full-width'}>
          <Switch
            key={property.name}
            checked={properties[index].value as boolean}
            onChange={(newValue: boolean | undefined) => {
              updateDataValueByIndex(index, !!newValue, properties)
            }}
          />
        </div>
      )
    case 'number':
      return (
        <div className={'content-full-width'}>
          <NumberInput
            key={property.name}
            value={properties[index].value as number}
            onChange={(newValue: string) => {
              updateDataValueByIndex(index, newValue ? parseInt(newValue, 10) : '', properties)
            }}
          />
        </div>
      )
    case 'object':
      if (property.objectDescription) {
        return <Fragment key={property.name}>{renderObjectFields(property)}</Fragment>
      }
      throw new Error('PropertyGenerator: You must set objectDescription for object type.')
    case 'array':
      if (property.objectDescription) {
        return <ArrayObjectRender key={property.name} property={property} updateData={updateData} />
      }
      throw new Error('PropertyGenerator: You must set objectDescription for array type.')
    case 'enum':
      if (property.enumValues) {
        // after deserialization, it's plain object. add normal checking
        if (!properties[index].enumSource?.fetch) {
          properties[index].enumSource = new BaseCrud<IPropertiesEnumValues>({
            fetcher: new Static(property.enumValues),
            keyField: 'key',
          })
        }
        return (
          <div className={'content-full-width'} key={property.name}>
            <Dropdown
              source={properties[index].enumSource as BaseCrud<IPropertiesEnumValues>}
              caption={'value'}
              label={property.name}
              onChange={(newValue: string) => {
                updateDataValueByIndex(index, newValue, properties)
              }}
              value={properties[index].value as string}
            />
          </div>
        )
      }
      throw new Error('PropertyGenerator: You must set enumValues for enum type.')
    case 'custom':
      const editorName = property.customEditor
      if (editorName) {
        const renders = {
          defaultRender: () => (
            <DefaultRender property={property} value={`${property.value}`} onChange={onChange} />
          ),
          arrayRender: () => (
            <ArrayObjectRender key={property.name} property={property} updateData={updateData} />
          ),
        }

        return lazyRenderWidget(editorName, {
          ...renders,
          properties,
          label: property.name,
          value: properties[index].value,
          valueChanged: (newValue: TPropertiesValue) => {
            updateDataValueByIndex(index, newValue, properties)
          },
        })
      }

      throw new Error('PropertyGenerator: You must set customEditor field for custom type.')
    default:
      return (
        <DefaultRender
          key={property.name}
          property={property}
          value={`${property.value}`}
          onChange={onChange}
        />
      )
  }
}
